import getJobStatusOrSubStatusLabel from "@utils/getJobStatusOrSubStatusLabel"

import { Table } from "@organisms"
import TableView from "@organisms/ObjectsView/TableView/TableView"

export default function JobTableView() {
    return (
        <TableView.Table>
            <Table.Column<Job>
                accessorKey="id"
                header="ID"
                size={144}
                cell={({ row }) => <Table.Cells.IDCell id={row.original.custom_id || row.original.id} />}
                enableSorting={true}
                enablePinning={true}
                dataType="alphabetical"
            />
            <Table.Column<Job>
                accessorKey="service_name"
                header="Service"
                size={224}
                cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                enableSorting={true}
                enablePinning={true}
                dataType="alphabetical"
            />
            <Table.Column<Job>
                accessorKey="state_label"
                header="State"
                size={128}
                cell={({ getValue }) => <Table.Cells.BadgesCell badges={[getValue()]} />}
                enableSorting={false}
                enablePinning={false}
                dataType="other"
                loadingType="badge"
            />
            <Table.Column<Job>
                accessorKey="status_label"
                header="Status"
                size={240}
                cell={({ row }) => (
                    <Table.Cells.StatusCell
                        objectType="job"
                        status={row.original.status_label}
                        statusLabelOverride={getJobStatusOrSubStatusLabel(row.original)}
                        statusColorOverride={row.original.status_color_override}
                    />
                )}
                enableSorting={true}
                enablePinning={false}
                dataType="other"
                loadingType="badge"
            />
            <Table.Column<Job>
                accessorKey="job_type"
                header="Job Type"
                size={128}
                cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                enableSorting={false}
                enablePinning={false}
                dataType="other"
            />
            <Table.Column<Job>
                accessorKey="priority"
                header="Priority"
                size={144}
                cell={({ getValue }) => <Table.Cells.PriorityCell priority={getValue()} />}
                enableSorting={true}
                enablePinning={true}
                dataType="other"
            />
            <Table.Column<Job>
                accessorKey="origin_type_label"
                header="Source"
                size={184}
                cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="alphabetical"
            />
            <Table.Column<Job>
                accessorKey="origin_id"
                header="Source ID"
                size={144}
                cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="alphabetical"
            />
            <Table.Column<Job>
                accessorKey="assigned_technicians"
                header="Techs"
                size={80}
                cell={({ getValue }) => <Table.Cells.UserAvatarCell users={getValue()} />}
                enableSorting={false}
                enablePinning={false}
                dataType="other"
                loadingType="avatar"
            />
            <Table.Column<Job>
                accessorKey="date_received"
                header="Date Received"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="client_name"
                header="Client"
                size={224}
                cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="alphabetical"
            />
            <Table.Column<Job>
                accessorKey="service_location_name"
                header="Location Name"
                size={288}
                cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="alphabetical"
            />
            <Table.Column<Job>
                accessorKey="service_location_address"
                header="Location Address"
                size={288}
                cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="alphabetical"
            />
            <Table.Column<Job>
                accessorKey="labels"
                header="Labels"
                size={240}
                cell={({ getValue }) => (
                    <Table.Cells.BadgesCell
                        badges={(getValue() as JobLabel[])?.map((label: JobLabel) => label.name)}
                    />
                )}
                enableSorting={false}
                enablePinning={false}
                dataType="other"
                loadingType="badge"
            />
            <Table.Column<Job>
                accessorKey="start_time"
                header="Scheduled For"
                size={224}
                cell={({ getValue, row }) => (
                    <Table.Cells.DateTimeCell
                        value={getValue()}
                        showTime={true}
                        isDangerous={
                            ["Pending", "Scheduled"].includes(row.original.state_label) && row.original.is_past_due
                        }
                    />
                )}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="duration"
                header="Duration"
                size={112}
                cell={({ getValue }) => <Table.Cells.NumericalDataCell type="duration" value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="numerical"
                alignment="right"
            />
            <Table.Column<Job>
                accessorKey="last_scheduled_at"
                header="Scheduled"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="last_status_transition_time"
                header="Status Changed"
                size={160}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={false}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="last_modified_at"
                header="Last Modified"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="last_active_at"
                header="Active"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="last_paused_at"
                header="Paused"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="last_on_hold_at"
                header="On Hold"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="last_completed_at"
                header="Completed"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="last_finalized_at"
                header="Finalized"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
            <Table.Column<Job>
                accessorKey="last_cancelled_at"
                header="Cancelled"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
        </TableView.Table>
    )
}
